import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiUrl } from './config';
import { useAuth } from './contexts/AuthContext';
import { Transaction, AccountInfo } from './types';
import './App.css';

function Transactions() {
  const { accountId } = useParams<{ accountId: string }>();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [accountInfo, setAccountInfo] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  const fetchTransactions = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiUrl(`/api/transactions?account_id=${accountId}`),
        {
          headers: {
            ...getAuthHeaders(),
          },
        }
      );
      const data = await response.json();

      if (data.success) {
        setTransactions(data.transactions || []);
        setAccountInfo(data.account);
      } else {
        setError(data.error || 'Failed to fetch transactions');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setIsLoading(false);
    }
  }, [accountId, getAuthHeaders]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  if (isLoading) {
    return (
      <div className="App">
        <header className="App-header">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <div
              style={{
                width: '50px',
                height: '50px',
                border: '4px solid rgba(97, 218, 251, 0.3)',
                borderTop: '4px solid #61dafb',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}
            />
            <h2 style={{ fontSize: '18px', opacity: 0.8 }}>
              Loading transactions...
            </h2>
          </div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </header>
      </div>
    );
  }

  return (
    <div className="App">
      <header className="App-header">
        <button
          onClick={() => navigate('/')}
          style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            padding: '8px 16px',
            fontSize: '14px',
            borderRadius: '4px',
            border: 'none',
            backgroundColor: '#61dafb',
            color: '#282c34',
            cursor: 'pointer',
          }}
        >
          ← Back
        </button>

        {accountInfo && (
          <div style={{ marginBottom: '20px' }}>
            <h2>{accountInfo.name}</h2>
            {accountInfo.mask && (
              <p style={{ opacity: 0.7, fontSize: '14px' }}>
                ...{accountInfo.mask}
              </p>
            )}
          </div>
        )}

        {error && (
          <p style={{ color: '#ff6b6b', marginTop: '10px' }}>{error}</p>
        )}

        {transactions.length === 0 ? (
          <p>No transactions found</p>
        ) : (
          <div
            style={{
              width: '90%',
              maxWidth: '800px',
              marginTop: '20px',
            }}
          >
            <h3 style={{ textAlign: 'left', marginBottom: '15px' }}>
              Transactions ({transactions.length})
            </h3>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '10px',
              }}
            >
              {transactions.map((transaction) => (
                <div
                  key={transaction.transaction_id}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    textAlign: 'left',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {transaction.name}
                    </div>
                    {transaction.original_description &&
                      transaction.original_description !== transaction.name && (
                        <div
                          style={{
                            fontSize: '12px',
                            opacity: 0.7,
                            marginBottom: '4px',
                          }}
                        >
                          {transaction.original_description}
                        </div>
                      )}
                    <div style={{ fontSize: '12px', opacity: 0.7 }}>
                      {formatDate(transaction.date)}
                    </div>
                    {transaction.merchant_name && (
                      <div
                        style={{
                          fontSize: '12px',
                          opacity: 0.8,
                          marginTop: '4px',
                        }}
                      >
                        📍 {transaction.merchant_name}
                      </div>
                    )}
                    {transaction.personal_finance_category && (
                      <div
                        style={{
                          fontSize: '11px',
                          opacity: 0.6,
                          marginTop: '4px',
                        }}
                      >
                        {transaction.personal_finance_category.primary}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: '16px',
                      color: transaction.amount > 0 ? '#ff6b6b' : '#4caf50',
                      marginLeft: '20px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatAmount(transaction.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </header>
    </div>
  );
}

export default Transactions;
